import { ChevronRightIcon } from '@chakra-ui/icons';
import {
  Divider,
  Editable,
  EditableInput,
  EditablePreview,
  Flex,
  IconButton,
  Input,
  Spacer,
  Text,
  useEditableControls,
} from '@chakra-ui/react';
import { IconPencilLine } from 'central-icons/IconPencilLine';
import { startCase } from 'lodash';
import { useRef } from 'react';

import { FeatureFlag } from '@revelio/core';

import { useUnleashFlag } from '../../../shared/unleash-flags/use-unleash-flag';
import {
  GlobalTopRightContent,
  PoweredByRevelioLabsLogo,
} from '../../GlobalTopRightContent';
import { FilterId } from '../filters/types/filters';
import {
  type AnalyticsEntity,
  getAnalyticsEntityFromRevelioPageId,
} from '../utils/revelio-primary-filter-entity-switch';
import {
  getPrimaryFilterIdForScreenerV2Page,
  isScreenerV2Page,
} from '../utils/routing-helpers';
import { AnalyticsViewTabs } from './AnalyticsViewTabs';
import { useEditMode } from './EditModeProvider/EditModeProvider';
import { PageActionBar } from './PageActionBar/PageActionBar';
import {
  usePageConfig,
  useTabContentConfig,
} from './PageConfigProvider/PageConfigProvider';
import { RevelioPageId } from './PageConfigProvider/types';

export const Header = ({
  isReportPage = false,
}: {
  isReportPage?: boolean;
}) => {
  const { config: fullConfig, dispatch: fullConfigDispatch } = usePageConfig();
  const { name, revelioPageId } = fullConfig;

  const { config: tabContent } = useTabContentConfig();

  const isNewDashboardLayout = useUnleashFlag(FeatureFlag.DashboardNewLayout);
  const analyticsEntity = getAnalyticsEntityFromRevelioPageId(revelioPageId);

  const primaryFilterId =
    tabContent.primaryFilter?.id ??
    getPrimaryFilterIdForScreenerV2Page(revelioPageId);

  // Show the tab bar on custom-dash pages too (not just analytics views) so
  // users can hop between any tab without leaving the header. Discovery /
  // screener / report pages still opt out.
  const customPageEntity: AnalyticsEntity | undefined = (() => {
    if (analyticsEntity || revelioPageId || isReportPage) return undefined;
    if (primaryFilterId === FilterId.COMPANY) return 'company';
    if (primaryFilterId === FilterId.GEOGRAPHY) return 'geography';
    if (primaryFilterId === FilterId.ROLE) return 'role';
    return undefined;
  })();
  const tabBarEntity = analyticsEntity ?? customPageEntity;

  const primaryFilterLabel = (() => {
    if (primaryFilterId) return startCase(primaryFilterId.toLowerCase());
    if (revelioPageId === RevelioPageId.TALENT_DISCOVERY) return 'Individual';
    return null;
  })();

  const isDiscoveryPage =
    revelioPageId === RevelioPageId.TALENT_DISCOVERY ||
    isScreenerV2Page(revelioPageId);

  const { editMode } = useEditMode();

  const inputRef = useRef<HTMLInputElement>(null);

  const handleSubmit = (value: string) => {
    const finalTitle = value.trim() || 'Untitled';

    if (finalTitle !== name) {
      fullConfigDispatch({ type: 'UPDATE_PAGE_TITLE', payload: finalTitle });
    }
  };

  if (isNewDashboardLayout) {
    const titleNode = (() => {
      if (tabBarEntity) {
        return (
          <Text
            fontSize="1.25rem"
            fontWeight="600"
            lineHeight="32px"
            whiteSpace="nowrap"
          >
            {`${primaryFilterLabel ?? ''} Analytics`.trim()}
          </Text>
        );
      }
      if (isDiscoveryPage && primaryFilterLabel) {
        return (
          <Text
            fontSize="1.25rem"
            fontWeight="600"
            lineHeight="32px"
            whiteSpace="nowrap"
          >
            {`${primaryFilterLabel} Discovery`}
          </Text>
        );
      }
      return (
        <Flex alignItems="center">
          {primaryFilterLabel && (
            <>
              <Text
                fontSize="1.25rem"
                fontWeight="600"
                lineHeight="32px"
                marginRight="3px"
                whiteSpace="nowrap"
              >
                {primaryFilterLabel}
              </Text>
              <ChevronRightIcon
                boxSize="5"
                color="text.primary"
                transform="translateY(1px)"
              />
            </>
          )}
          {editMode ? (
            <Editable
              key={name}
              defaultValue={name}
              onSubmit={handleSubmit}
              submitOnBlur
              display="flex"
              alignItems="center"
            >
              <EditablePreview
                fontSize="1.25rem"
                fontWeight="600"
                lineHeight="28px"
                cursor="pointer"
                py="2px"
                _hover={{ bg: 'gray.100' }}
              />
              <Input
                ref={inputRef}
                as={EditableInput}
                height="32px"
                lineHeight="32px"
                fontSize="1.25rem"
                variant="flushed"
              />
              <EditButton />
            </Editable>
          ) : (
            <Text
              fontSize="1.25rem"
              fontWeight="600"
              lineHeight="32px"
              whiteSpace="nowrap"
            >
              {name}
            </Text>
          )}
        </Flex>
      );
    })();

    return (
      <>
        <Flex
          role="group"
          alignItems="center"
          minHeight="32px"
          justifyContent="space-between"
          gap={6}
          data-testid="page-header-label"
        >
          {titleNode}
          <Spacer />
          {tabBarEntity && (
            <>
              <AnalyticsViewTabs entity={tabBarEntity} />
              <Divider
                orientation="vertical"
                borderColor="gray.300"
                height="20px"
                // Asymmetric to compensate for the action button's internal
                // 4px left padding
                marginLeft={-2}
                marginRight={-3}
              />
            </>
          )}
          <GlobalTopRightContent showPoweredBy={!tabBarEntity}>
            <PageActionBar isReportPage={isReportPage} />
          </GlobalTopRightContent>
        </Flex>
        <Divider borderColor="gray.300" />
        {tabBarEntity && (
          <Flex justifyContent="flex-end">
            <PoweredByRevelioLabsLogo />
          </Flex>
        )}
      </>
    );
  }

  return (
    <>
      <Flex
        role="group"
        alignItems="center"
        marginBottom={2}
        minHeight="32px"
        justifyContent="space-between"
        data-testid="page-header-label"
      >
        <Flex alignItems="center">
          {primaryFilterLabel && (
            <>
              <Text
                fontSize="1.25rem"
                fontWeight="600"
                lineHeight="32px"
                marginRight="3px"
                whiteSpace="nowrap"
              >
                {primaryFilterLabel}
              </Text>

              <ChevronRightIcon
                boxSize="5"
                color="text.primary"
                transform="translateY(1px)"
              />
            </>
          )}
          {editMode ? (
            <Editable
              key={name}
              defaultValue={name}
              onSubmit={handleSubmit}
              submitOnBlur
              display="flex"
              alignItems="center"
            >
              <EditablePreview
                fontSize="1.25rem"
                fontWeight="600"
                lineHeight="28px"
                cursor="pointer"
                py="2px"
                _hover={{
                  bg: 'gray.100',
                }}
              />
              <Input
                ref={inputRef}
                as={EditableInput}
                height="32px"
                lineHeight="32px"
                fontSize="1.25rem"
                variant="flushed"
              />
              <EditButton />
            </Editable>
          ) : (
            <Text
              fontSize="1.25rem"
              fontWeight="600"
              lineHeight="32px"
              whiteSpace="nowrap"
            >
              {name}
            </Text>
          )}
        </Flex>
        <Spacer />
        <GlobalTopRightContent>
          <PageActionBar isReportPage={isReportPage} />
        </GlobalTopRightContent>
      </Flex>
      <Divider {...(isReportPage && { marginBottom: '12px' })} />
    </>
  );
};

const EditButton = () => {
  const { isEditing, getEditButtonProps } = useEditableControls();

  if (isEditing) return null;

  return (
    <IconButton
      size="xs"
      icon={<IconPencilLine size={16} />}
      aria-label="Edit page name"
      variant="ghost"
      opacity={0}
      marginLeft="1"
      _groupHover={{ opacity: 1, background: 'transparent' }}
      _focus={{ boxShadow: 'none' }}
      transition="opacity 0.2s ease-in-out"
      {...getEditButtonProps()}
    />
  );
};
